/**
 * QuickBook rejection-code → user copy (single source of truth).
 *
 * Why this file exists
 * --------------------
 * The BookAppointment BE has 6 distinct rejection codes (per
 * `BookAppointmentCommand.cs` precondition chain 2b.1 – 2b.5 + the
 * engine re-validation). The FE shows each rejection across THREE
 * surfaces:
 *
 *   1. Toast immediately on Book click
 *   2. Inline banner in the slot picker after dropping to step 3
 *   3. Empty-state message when the post-release re-query has no slots
 *
 * Prior to this file each surface lived in its own switch-case in
 * its own component, so the three drifted out of sync (B26 + B30 +
 * the G1 STAFF_NOT_AT_BRANCH gap all stem from that drift). Now all
 * three surfaces consume `getRejectionUx(code, ctx)` which returns
 * one shaped object — easy to grep, easy to test, impossible to
 * drift.
 *
 * The audit doc at devanza-claude/docs/audits/calendar-v1-booking-audit.md
 * Part A.3 is the source-of-truth matrix this helper implements.
 */

import type { CSSProperties } from 'react';

/**
 * Distinct slot-rejection codes recognised by the booking surfaces.
 *
 * DUAL-RECOGNITION (AB#1920 / WS-1b.1 — PR-J0 prerequisite)
 * ---------------------------------------------------------
 * The BE is flipping its booking error literals from the legacy
 * `Slot.*` / bare-literal family to the canonical `DOMAIN.CODE`
 * registry codes (`APPOINTMENT.*`, `CUSTOMER.*`, `STAFF.*`). The flip
 * (PR-J0) and the FE must NOT be coupled in lock-step, so during the
 * one-sprint deprecation window the FE recognises BOTH spellings and
 * resolves them to the *same* UX. The WS-5b RFC-7807 normalizer only
 * reconciles the generic i18n *message* (errors.json); it does NOT
 * touch these *behavioural* checks (drop-to-picker, lock-release,
 * per-surface copy). Hence the dual recognition lives here.
 *
 * This union therefore lists BOTH the legacy literal and its canonical
 * equivalent. Both remain valid; neither is removed until the BE flip
 * has shipped and the deprecation window closes (tracked in
 * contracts/error-codes.yaml — the `deprecated:` alias rows).
 */
export type BookingRejectionCode =
    // Legacy literals (BE still emits these until PR-J0 flips the emitter).
    | 'Slot.BRANCH_CLOSED'
    | 'Slot.OUTSIDE_SHIFT'
    | 'Slot.STAFF_NOT_AT_BRANCH'
    | 'Slot.STAFF_CANT_SERVICE'
    | 'Slot.GENDER_MISMATCH'
    | 'Slot.CONFLICT'
    // Canonical equivalents (BE emits these after PR-J0 — recognised NOW).
    | 'APPOINTMENT.BRANCH_CLOSED'
    | 'APPOINTMENT.OUTSIDE_SHIFT'
    | 'APPOINTMENT.STAFF_NOT_AT_BRANCH'
    | 'APPOINTMENT.STAFF_CANT_SERVICE'
    | 'APPOINTMENT.GENDER_MISMATCH'
    | 'APPOINTMENT.CONFLICT'
    // Side-channel codes that don't come from the slot family but still
    // need user-facing copy.
    | 'Service.NoPricing'
    | 'Slot.InPast';

/**
 * Legacy literal → canonical registry code (AB#1920 / WS-1b.1).
 *
 * The canonical spellings are read line-for-line from the error-code
 * registry (`devanza-claude/contracts/error-codes.yaml`, the
 * `aliasOf:` rows). `normalizeRejectionCode` folds a canonical code
 * back onto its legacy literal so every downstream lookup (the
 * `switch`, the two membership Sets) is written once against the legacy
 * key and recognises both spellings for free.
 *
 * NOTE on the gender code: the registry canonical is
 * `APPOINTMENT.GENDER_MISMATCH` (NOT `SLOT_GENDER_MISMATCH`) — verified
 * against contracts/error-codes.yaml `Slot.GENDER_MISMATCH.aliasOf`.
 *
 * This map covers all 9 booking literals the BE flips in PR-J0. The
 * four non-Slot ones (the CUSTOMER and STAFF gender/reassign literals)
 * aren't part of the QuickBook slot-picker family but are mapped here so
 * any consumer that normalises a booking `code` through this module gets
 * the same dual recognition. Keep in sync with the registry alias rows.
 */
export const LEGACY_TO_CANONICAL: Readonly<Record<string, string>> = {
    // Slot.* booking family (QuickBook slot picker).
    'Slot.BRANCH_CLOSED': 'APPOINTMENT.BRANCH_CLOSED',
    'Slot.OUTSIDE_SHIFT': 'APPOINTMENT.OUTSIDE_SHIFT',
    'Slot.STAFF_NOT_AT_BRANCH': 'APPOINTMENT.STAFF_NOT_AT_BRANCH',
    'Slot.STAFF_CANT_SERVICE': 'APPOINTMENT.STAFF_CANT_SERVICE',
    'Slot.GENDER_MISMATCH': 'APPOINTMENT.GENDER_MISMATCH',
    'Slot.CONFLICT': 'APPOINTMENT.CONFLICT',
    // Gender-policy + reassignment literals (other booking surfaces).
    CUSTOMER_GENDER_REQUIRED: 'CUSTOMER.GENDER_REQUIRED',
    CUSTOMER_GENDER_MISMATCHES_SALON: 'CUSTOMER.GENDER_MISMATCH',
    STAFF_GENDER_MISMATCHES_SALON: 'STAFF.GENDER_MISMATCH',
    STAFF_REASSIGN_HAS_ACTIVE_BOOKINGS: 'STAFF.REASSIGN_HAS_ACTIVE_BOOKINGS',
};

/**
 * Canonical → legacy literal (reverse of `LEGACY_TO_CANONICAL`). Used
 * to fold a canonical code back onto the legacy spelling the
 * `getRejectionUx` switch and the membership Sets are keyed on, so a
 * single set of arms / entries handles both spellings.
 */
const CANONICAL_TO_LEGACY: Readonly<Record<string, string>> = Object.freeze(
    Object.fromEntries(
        Object.entries(LEGACY_TO_CANONICAL).map(([legacy, canonical]) => [
            canonical,
            legacy,
        ]),
    ),
);

/**
 * Fold a rejection code onto its LEGACY spelling so a single set of
 * switch arms / Set entries recognises both legacy and canonical.
 *
 * - Legacy literal (`Slot.BRANCH_CLOSED`) → returned unchanged.
 * - Canonical code (`APPOINTMENT.BRANCH_CLOSED`) → folded to the legacy
 *   literal it aliases.
 * - Anything else (side-channel codes, unknown codes) → unchanged, so
 *   the existing fallthrough behaviour is preserved exactly.
 *
 * Folding toward legacy (rather than canonical) keeps the switch arms
 * and Set entries byte-for-byte identical to before — the legacy path
 * is provably untouched, and canonical is layered on top additively.
 */
export function normalizeRejectionCode(code: string): string {
    return CANONICAL_TO_LEGACY[code] ?? code;
}

/**
 * All slot-family rejections that should drop the modal to step 3
 * (picker). Lists BOTH the legacy literal and its canonical equivalent
 * so `SLOT_REJECTIONS.has(code)` is true whichever spelling the BE
 * sends during the deprecation window.
 */
export const SLOT_REJECTIONS: ReadonlySet<string> = new Set<string>([
    // Legacy literals.
    'Slot.BRANCH_CLOSED',
    'Slot.OUTSIDE_SHIFT',
    'Slot.STAFF_NOT_AT_BRANCH',
    'Slot.STAFF_CANT_SERVICE',
    'Slot.GENDER_MISMATCH',
    'Slot.CONFLICT',
    // Canonical equivalents (dual-recognition).
    'APPOINTMENT.BRANCH_CLOSED',
    'APPOINTMENT.OUTSIDE_SHIFT',
    'APPOINTMENT.STAFF_NOT_AT_BRANCH',
    'APPOINTMENT.STAFF_CANT_SERVICE',
    'APPOINTMENT.GENDER_MISMATCH',
    'APPOINTMENT.CONFLICT',
]);

/**
 * Codes that release the staff lock when dropping to the picker.
 *
 * Reasoning per code:
 *   - STAFF_CANT_SERVICE: staff lacks capability → others might have it.
 *   - GENDER_MISMATCH: staff fails gender rule → others might match.
 *   - OUTSIDE_SHIFT: staff is off → others might be on.
 *   - STAFF_NOT_AT_BRANCH: staff isn't enrolled here → others might be.
 *     (G1 fix — this was missing before.)
 *   - CONFLICT: staff IS at work, just already booked. Keeping the lock
 *     lets the owner pick a different time for THE SAME staff, which is
 *     usually the intent. Lock stays held.
 *   - BRANCH_CLOSED: the date is the problem, not the staff. Lock stays
 *     held (or rather, irrelevant — different date is the answer).
 *
 * Lists BOTH legacy and canonical spellings (dual-recognition) so the
 * lock-release semantics are identical whichever the BE sends.
 */
export const LOCK_RELEASE_CODES: ReadonlySet<string> = new Set<string>([
    // Legacy literals.
    'Slot.STAFF_CANT_SERVICE',
    'Slot.GENDER_MISMATCH',
    'Slot.OUTSIDE_SHIFT',
    'Slot.STAFF_NOT_AT_BRANCH',
    // Canonical equivalents (dual-recognition).
    'APPOINTMENT.STAFF_CANT_SERVICE',
    'APPOINTMENT.GENDER_MISMATCH',
    'APPOINTMENT.OUTSIDE_SHIFT',
    'APPOINTMENT.STAFF_NOT_AT_BRANCH',
]);

/**
 * Context the copy templates can interpolate. All optional — the helper
 * substitutes sensible fallbacks ("the staff", "this service", etc.)
 * when a value is missing.
 */
export interface RejectionContext {
    staffName?: string;
    serviceName?: string;
    branchName?: string;
    /** "HH:mm" — the time the user clicked / asked for. */
    around?: string | null;
}

/**
 * The full UX bundle for a single rejection code. Components consume
 * whichever surface they render: `toast` for the immediate error,
 * `banner` for the picker preamble, `emptyTitle`+`emptyHint` for the
 * "no slots match" state.
 */
export interface RejectionUx {
    /** One-line error toast shown immediately on Book failure. */
    toast: string;
    /**
     * Two-sentence banner shown above the slot picker AFTER the lock
     * has been released. `null` for codes that DON'T release the lock
     * (BRANCH_CLOSED, CONFLICT) — those keep the picker scoped to the
     * locked staff and the banner is redundant.
     */
    banner: string | null;
    /** Headline for the empty-state when the re-query returns zero slots. */
    emptyTitle: string;
    /** Sub-text under the empty-state headline. Includes the actionable next step. */
    emptyHint: string;
}

/**
 * Format helpers — keep the interpolation tidy and fallback-safe.
 */
function s(name?: string, fallback = 'the staff'): string {
    return name && name.trim() ? name : fallback;
}
function svc(name?: string, fallback = 'this service'): string {
    return name && name.trim() ? name : fallback;
}
function br(name?: string, fallback = 'this branch'): string {
    return name && name.trim() ? name : fallback;
}
function at(time?: string | null): string {
    return time && time.trim() ? time : 'that time';
}

/**
 * Single dispatch — every component consumes this and gets the exact
 * three messages they need. Update the matrix here when BE adds a new
 * rejection code, and every surface picks it up automatically.
 *
 * DUAL-RECOGNITION: the incoming `code` is normalised to its legacy
 * spelling first (`normalizeRejectionCode`), so a canonical code
 * (`APPOINTMENT.BRANCH_CLOSED`) and its legacy literal
 * (`Slot.BRANCH_CLOSED`) hit the SAME switch arm and return identical
 * UX. The switch arms remain keyed on the legacy literals — the legacy
 * path is unchanged, canonical is folded onto it (AB#1920 / WS-1b.1).
 */
export function getRejectionUx(
    code: string,
    ctx: RejectionContext = {},
    /**
     * O3 (SO-38) — optional `t()` from LanguageContext. When given, every surface
     * reads `qbr.<code>.<surface>` (with {staff}/{service}/{branch}/{time}) and falls
     * back to the English below when a key is missing. Without it the English
     * matrix is returned unchanged (the pinned tests below rely on that).
     */
    translate?: (key: string) => string,
): RejectionUx {
    if (translate) return translateUx(code, ctx, translate);
    return englishUx(code, ctx);
}

const UX_KEY_BY_CODE: Record<string, string> = {
    'Slot.BRANCH_CLOSED': 'branchClosed',
    'Slot.OUTSIDE_SHIFT': 'outsideShift',
    'Slot.STAFF_NOT_AT_BRANCH': 'staffNotAtBranch',
    'Slot.STAFF_CANT_SERVICE': 'staffCantService',
    'Slot.GENDER_MISMATCH': 'genderMismatch',
    'Slot.CONFLICT': 'conflict',
    'Service.NoPricing': 'noPricing',
    'Slot.InPast': 'inPast',
};

function translateUx(code: string, ctx: RejectionContext, t: (key: string) => string): RejectionUx {
    const english = englishUx(code, ctx);
    const base = `qbr.${UX_KEY_BY_CODE[normalizeRejectionCode(code)] ?? 'unknown'}`;
    const pick = (key: string, fallback: string) => {
        const v = t(key);
        return v && v !== key ? v : fallback;
    };
    const vars: Record<string, string> = {
        staff: ctx.staffName?.trim() || pick('qbr.fb.staff', 'the staff'),
        service: ctx.serviceName?.trim() || pick('qbr.fb.service', 'this service'),
        branch: ctx.branchName?.trim() || pick('qbr.fb.branch', 'this branch'),
        time: ctx.around?.trim() || pick('qbr.fb.time', 'that time'),
    };
    const fill = (template: string) => template.replace(/\{(staff|service|branch|time)\}/g, (_, k: string) => vars[k]);
    const surface = (name: keyof RejectionUx, fallback: string | null) => {
        if (fallback === null) return null;
        const key = `${base}.${name}`;
        const v = t(key);
        return v && v !== key ? fill(v) : fallback;
    };
    return {
        toast: surface('toast', english.toast) as string,
        banner: surface('banner', english.banner),
        emptyTitle: surface('emptyTitle', english.emptyTitle) as string,
        emptyHint: surface('emptyHint', english.emptyHint) as string,
    };
}

function englishUx(code: string, ctx: RejectionContext): RejectionUx {
    const staff = s(ctx.staffName);
    const service = svc(ctx.serviceName);
    const branch = br(ctx.branchName);
    const time = at(ctx.around);

    switch (normalizeRejectionCode(code)) {
        case 'Slot.BRANCH_CLOSED':
            // Epic AB#1824 / AB#1831 — Hint repointed to the new single-source
            // matrix on Business Details (the per-branch Hours editor on the
            // Branch card is decommissioned). EN+AR via the rejection.* i18n
            // namespace; surface-level copy stays inline here for the picker
            // empty state (matches the existing pattern for the other 5
            // rejection codes — i18n batch lifts them together later).
            return {
                toast: 'The branch is closed on this day.',
                banner: null,
                emptyTitle: `${branch} is closed on this day.`,
                emptyHint: 'Pick a different date. To change opening hours, go to Business Details → Hours.',
            };

        case 'Slot.OUTSIDE_SHIFT':
            return {
                toast: `${staff} isn't scheduled to work at that time.`,
                banner: `${staff} isn't on shift around ${time}. Showing other staff at ${branch} who can perform ${service}.`,
                emptyTitle: `Nobody at ${branch} is on shift around ${time} on this day.`,
                emptyHint: `${staff} isn't scheduled, and no other staff at this branch is either. Try a different time, a different date, or set staff schedules in Manage Staff → Schedule.`,
            };

        case 'Slot.STAFF_NOT_AT_BRANCH':
            return {
                toast: `${staff} isn't assigned to ${branch}.`,
                banner: `${staff} isn't enrolled at ${branch}. Showing other staff at this branch who can perform ${service}.`,
                emptyTitle: `No staff enrolled at ${branch} can perform ${service}.`,
                emptyHint: 'Enroll a staff member at this branch via Manage Staff → Branches, or grant the service to a different staff member.',
            };

        case 'Slot.STAFF_CANT_SERVICE':
            return {
                toast: `${staff} doesn't perform ${service}.`,
                banner: `${staff} can't perform ${service}. Showing other staff at ${branch} who can.`,
                emptyTitle: `No staff at ${branch} can perform ${service}.`,
                emptyHint: `${staff} doesn't have it as a capability, and neither does any other staff at this branch. Grant the service to a staff member in Manage Staff → Services, then come back.`,
            };

        case 'Slot.GENDER_MISMATCH':
            return {
                toast: `${service} can't be performed by ${staff} due to a gender restriction.`,
                banner: `${staff} doesn't match the gender requirements for ${service}. Showing other staff at ${branch} who can.`,
                emptyTitle: `No staff at ${branch} matches the gender requirements for ${service}.`,
                emptyHint: `${staff} doesn't match, and no other staff at this branch does either. Either change the rule in Services → ${service}, or add staff who match.`,
            };

        case 'Slot.CONFLICT':
            return {
                toast: 'That slot was just taken. Please pick another.',
                banner: null,
                emptyTitle: `${staff} has no free slots that fit ${service} around ${time}.`,
                emptyHint: 'Try a different time, or pick a different staff member.',
            };

        case 'Service.NoPricing':
            return {
                toast: `${service} has no pricing options. Add a price tier in Services first.`,
                banner: null,
                emptyTitle: `${service} has no pricing options.`,
                emptyHint: 'Add at least one pricing tier in Services → Edit → Pricing tiers, then come back.',
            };

        case 'Slot.InPast':
            return {
                toast: 'Cannot book in the past. Pick a current or future time.',
                banner: null,
                emptyTitle: 'No slots available for past dates.',
                emptyHint: 'Pick today or a future date.',
            };

        default:
            // Unknown code — surface the BE message via the toast layer
            // (handled by the caller) and show a generic empty state.
            return {
                toast: 'Booking failed. Please try again.',
                banner: null,
                emptyTitle: 'No available slots match your filters.',
                emptyHint: 'Try a different date or time.',
            };
    }
}

/* eslint-disable @typescript-eslint/no-unused-vars */
// Reserved for a future "deep-link" enhancement (G9 in the audit) — the
// empty-state hints currently spell out routes like "Manage Staff →
// Services" as plain text. When we wire those to real navigation,
// extend RejectionUx with an optional `nextRoute?: { label, to }` and
// return it here per code. Tracked as a follow-up in the audit doc.
type _ReservedForDeepLink = CSSProperties; // anchor type so import isn't dropped
/* eslint-enable @typescript-eslint/no-unused-vars */
